// shell/components/entity-fields/field-registry.ts

import { IDisposable } from '../types';
import type {
    ViewFieldDef,
    FormFieldDef,
    CustomViewFieldController,
    CustomFormFieldController,
} from '../entity-view/types';
import {
    renderTextView,
    renderNumberView,
    renderDateView,
    renderBooleanView,
    renderBadgeView,
    renderTagListView,
    renderEntityLinkView,
    renderHtmlView,
    renderFilesView,
} from './view-renderers';
import {
    renderTextInput,
    renderNumberInput,
    renderBooleanInput,
    renderDateInput,
    renderSelectInput,
    renderCheckboxGroupInput,
    renderDurationInput,
    renderSliderInput,
    renderEntitySearchInput,
    renderTagInput,
    renderFileInput,
    renderRichTextInput,
} from './form-renderers';
import type { FormRendererResult } from './form-renderers';

// ============================================================================
// Types
// ============================================================================

/**
 * View renderer function signature.
 * Returns an HTMLElement for display.
 */
export type ViewRenderer<T = unknown> = (
    field: ViewFieldDef<T>,
    value: unknown,
    model: T
) => HTMLElement;

/**
 * View renderer result with optional cleanup.
 */
export interface ViewRenderResult {
    element: HTMLElement;
    dispose?: () => void;
}

// ============================================================================
// View Field Registry
// ============================================================================

/**
 * Registry for view field renderers.
 * Maps field type to renderer function.
 */
class ViewFieldRegistry {
    private readonly renderers = new Map<string, ViewRenderer<unknown>>();

    constructor() {
        // Register built-in renderers
        this.register('text', renderTextView as ViewRenderer);
        this.register('number', renderNumberView as ViewRenderer);
        this.register('date', renderDateView as ViewRenderer);
        this.register('boolean', renderBooleanView as ViewRenderer);
        this.register('badge', renderBadgeView as ViewRenderer);
        this.register('tag-list', renderTagListView as ViewRenderer);
        this.register('entity-link', renderEntityLinkView as ViewRenderer);
        this.register('html', renderHtmlView as ViewRenderer);
        this.register('files', renderFilesView as ViewRenderer);
    }

    /**
     * Register a custom view renderer.
     */
    register(type: string, renderer: ViewRenderer): void {
        this.renderers.set(type, renderer);
    }

    /**
     * Unregister a view renderer.
     */
    unregister(type: string): void {
        this.renderers.delete(type);
    }

    /**
     * Get a renderer for a field type.
     */
    getRenderer(type: string): ViewRenderer | undefined {
        return this.renderers.get(type);
    }

    /**
     * Check if a renderer exists for a type.
     */
    hasRenderer(type: string): boolean {
        return this.renderers.has(type);
    }

    /**
     * Render a field.
     * Returns the element and optional dispose function.
     */
    render<T>(
        field: ViewFieldDef<T>,
        value: unknown,
        model: T
    ): ViewRenderResult {
        // Handle custom fields
        if (field.type === 'custom') {
            return this.renderCustomField(field, value, model);
        }

        // Get registered renderer
        const renderer = this.renderers.get(field.type);
        if (!renderer) {
            console.warn(`No view renderer registered for type: ${field.type}`);
            return {
                element: this.renderUnknownType(field.type),
            };
        }

        return {
            element: renderer(field as ViewFieldDef<unknown>, value, model),
        };
    }

    /**
     * Render a custom field using its factory.
     */
    private renderCustomField<T>(
        field: ViewFieldDef<T> & { type: 'custom' },
        value: unknown,
        model: T
    ): ViewRenderResult {
        const container = document.createElement('div');
        container.className = 'ev-field-view ev-field-view--custom';

        let controller: CustomViewFieldController<T> | null = null;

        try {
            controller = field.create(container);
            controller.render(value, model);
        } catch (error) {
            console.error('Error rendering custom field:', error);
            container.classList.add('ev-field-view--error');
            container.textContent = 'Error rendering field';
        }

        return {
            element: container,
            dispose: () => controller?.dispose?.(),
        };
    }

    /**
     * Render placeholder for unknown field type.
     */
    private renderUnknownType(type: string): HTMLElement {
        const el = document.createElement('div');
        el.className = 'ev-field-view ev-field-view--unknown';
        el.textContent = `Unknown field type: ${type}`;
        return el;
    }
}

// Singleton instance
export const viewFieldRegistry = new ViewFieldRegistry();

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Register a custom view renderer.
 */
export function registerViewRenderer(type: string, renderer: ViewRenderer): void {
    viewFieldRegistry.register(type, renderer);
}

/**
 * Render a view field.
 */
export function renderViewField<T>(
    field: ViewFieldDef<T>,
    value: unknown,
    model: T
): ViewRenderResult {
    return viewFieldRegistry.render(field, value, model);
}

// ============================================================================
// Form Renderer Types
// ============================================================================

/**
 * Form renderer function signature.
 */
export type FormRenderer<T = unknown> = (
    field: FormFieldDef<T>,
    initialValue: unknown,
    onChange: (value: unknown) => void
) => FormRendererResult;

// ============================================================================
// Form Field Registry
// ============================================================================

/**
 * Registry for form field renderers.
 * Maps field type to renderer function.
 */
class FormFieldRegistry {
    private readonly renderers = new Map<string, FormRenderer<unknown>>();

    constructor() {
        // Register built-in renderers
        this.register('text', renderTextInput as FormRenderer);
        this.register('number', renderNumberInput as FormRenderer);
        this.register('boolean', renderBooleanInput as FormRenderer);
        this.register('date', renderDateInput as FormRenderer);
        this.register('select', renderSelectInput as FormRenderer);
        this.register('checkbox-group', renderCheckboxGroupInput as FormRenderer);
        this.register('duration', renderDurationInput as FormRenderer);
        this.register('slider', renderSliderInput as FormRenderer);
        this.register('entity-search', renderEntitySearchInput as FormRenderer);
        this.register('tag-input', renderTagInput as FormRenderer);
        this.register('file-input', renderFileInput as FormRenderer);
        this.register('richtext', renderRichTextInput as FormRenderer);
    }

    /**
     * Register a custom form renderer.
     */
    register(type: string, renderer: FormRenderer): void {
        this.renderers.set(type, renderer);
    }

    /**
     * Unregister a form renderer.
     */
    unregister(type: string): void {
        this.renderers.delete(type);
    }

    /**
     * Get a renderer for a field type.
     */
    getRenderer(type: string): FormRenderer | undefined {
        return this.renderers.get(type);
    }

    /**
     * Check if a renderer exists for a type.
     */
    hasRenderer(type: string): boolean {
        return this.renderers.has(type);
    }

    /**
     * Render a form field.
     */
    render<T>(
        field: FormFieldDef<T>,
        initialValue: unknown,
        onChange: (value: unknown) => void
    ): FormRendererResult {
        // Handle custom fields
        if (field.type === 'custom') {
            return this.renderCustomField(field, initialValue, onChange);
        }

        // Get registered renderer
        const renderer = this.renderers.get(field.type);
        if (!renderer) {
            console.warn(`No form renderer registered for type: ${field.type}`);
            return this.renderUnknownType(field.type);
        }

        return renderer(field as FormFieldDef<unknown>, initialValue, onChange);
    }

    /**
     * Render a custom field using its factory.
     */
    private renderCustomField<T>(
        field: FormFieldDef<T> & { type: 'custom' },
        initialValue: unknown,
        onChange: (value: unknown) => void
    ): FormRendererResult {
        const container = document.createElement('div');
        container.className = 'ev-field-input ev-field-input--custom';

        let controller: CustomFormFieldController | null = null;

        try {
            controller = field.create(container, onChange);
            controller.render(initialValue);
        } catch (error) {
            console.error('Error rendering custom form field:', error);
            container.classList.add('ev-field-input--error');
            container.textContent = 'Error rendering field';
        }

        return {
            element: container,
            getValue: () => controller?.getValue() ?? null,
            setValue: (value: unknown) => controller?.render(value),
            validate: async (): Promise<true | string> => {
                const result = await controller?.validate?.();
                // Return true if valid, or the error message string
                if (result === true || result === undefined) {
                    return true;
                }
                // result is a string error message
                return result as string;
            },
            focus: () => controller?.focus?.(),
            dispose: () => controller?.dispose?.(),
        };
    }

    /**
     * Render placeholder for unknown field type.
     */
    private renderUnknownType(type: string): FormRendererResult {
        const el = document.createElement('div');
        el.className = 'ev-field-input ev-field-input--unknown';
        el.textContent = `Unknown field type: ${type}`;

        return {
            element: el,
            getValue: () => null,
            setValue: () => {},
            validate: async () => true,
            focus: () => el.focus(),
            dispose: () => {},
        };
    }
}

// Singleton instance
export const formFieldRegistry = new FormFieldRegistry();

// ============================================================================
// Form Convenience Functions
// ============================================================================

/**
 * Register a custom form renderer.
 */
export function registerFormRenderer(type: string, renderer: FormRenderer): void {
    formFieldRegistry.register(type, renderer);
}

/**
 * Render a form field.
 */
export function renderFormField<T>(
    field: FormFieldDef<T>,
    initialValue: unknown,
    onChange: (value: unknown) => void
): FormRendererResult {
    return formFieldRegistry.render(field, initialValue, onChange);
}
